import React, { useMemo } from "react";

import useSecuritiesBySecurityIds from "@fartherfinance/frontend/api/SecurityMaster/hooks/useSecuritiesBySecurityIds";
import { SecurityType } from "@fartherfinance/frontend/api/SecurityMaster/requests/Types";
import { SecurityId } from "@fartherfinance/frontend/api/Types";

import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";
import Chip from "@src/sharedComponents/Chip/Chip";
import Skeleton from "@src/sharedComponents/Skeleton/Skeleton";
import Tooltip from "@src/sharedComponents/Tooltip/Tooltip";

import styles from "./DnsIndicator.module.css";

interface EquivalentSecurityPillData {
  listIndex: number;
  listName: string;
}

interface Props {
  securityId: SecurityId;
  isDoNotSell?: boolean;
  isTaxLossHarvest?: boolean;
  equivalentSecurityPillData?: EquivalentSecurityPillData;
  securityDb?: {
    name: string | null;
    description: string | null;
    type: SecurityType;
    id: SecurityId;
    identifiers: {
      identifier: string;
      registrar:
        | "FARTHER"
        | "TICKER"
        | "CUSIP"
        | "ISIN"
        | "FIGI"
        | "BLOOMBERG"
        | "BLOOMBERG_COMPOSITE"
        | "REUTERS"
        | "SEDOL"
        | "SEC_CIK";
    }[];
  }[];
}

const SecurityCell: React.FC<Props> = (props) => {
  const auth = useAdvisorRequestAuth();

  const isInDb = useMemo(() => {
    const inDb = props.securityDb?.find((x) => x.id === props.securityId);
    return inDb !== undefined;
  }, [props.securityDb, props.securityId]);

  const securityRequest = useSecuritiesBySecurityIds(
    isInDb ? null : [props.securityId],
    auth
  );

  const securityInfo = useMemo(() => {
    const inDb = props.securityDb?.find((x) => x.id === props.securityId);
    if (inDb) {
      return inDb;
    }

    return securityRequest.data?.find((x) => x.id === props.securityId);
  }, [props.securityDb, props.securityId, securityRequest.data]);

  if (securityInfo === undefined) {
    return <Skeleton width={150} />;
  }

  const ticker = securityInfo.identifiers.find((x) => x.registrar === "TICKER");
  const cusip = securityInfo.identifiers.find((x) => x.registrar === "CUSIP");

  const identifier: string =
    ticker?.identifier ?? cusip?.identifier ?? securityInfo.name ?? "Unknown";

  const isMutualFund = securityInfo.type === "MutualFund";
  const isMoneyMarket = securityInfo.type === "CashLike";

  let warningMsg: string | null = null;
  if (isMutualFund) {
    warningMsg = " (Mutual Fund)"; // add prefix space
  } else if (isMoneyMarket) {
    warningMsg = " (Money Market)"; // add prefix space
  }

  // Define the tooltip text for Do Not Sell indicator
  const doNotSellTooltipText =
    "This security is marked as Do Not Sell. It will remain in the account but will not be rebalanced, traded, or used in tax-loss harvesting.";

  // Define the tooltip text for Tax Loss Harvest indicator
  const taxLossHarvestTooltipText =
    "This security is part of a tax-loss harvesting swap, where a losing position is sold and replaced with a similar security to realize tax losses.";

  // Define the tooltip text for ES indicator
  const equivalentSecuritiesTooltipText = `This security has a matching equivalent security in the list of proposed trades. It is part of ES list: ${
    props.equivalentSecurityPillData?.listName ?? ""
  }`;

  return (
    <div className={styles.securityCellContainer}>
      <Tooltip tooltipText={securityInfo.description ?? "Unknown"}>
        <div>
          {identifier}
          {warningMsg !== null ? (
            <span>
              <i>{warningMsg}</i>
            </span>
          ) : null}
        </div>
      </Tooltip>

      {props.isDoNotSell && (
        <Tooltip tooltipText={doNotSellTooltipText}>
          <div>
            <Chip size="small" label="DNS" className={styles.dnsChip} />
          </div>
        </Tooltip>
      )}

      {props.isTaxLossHarvest && (
        <Tooltip tooltipText={taxLossHarvestTooltipText}>
          <div>
            <Chip size="small" label="TLH" className={styles.dnsChip} />
          </div>
        </Tooltip>
      )}

      {props.equivalentSecurityPillData !== undefined && (
        <Tooltip tooltipText={equivalentSecuritiesTooltipText}>
          <div>
            <Chip
              size="small"
              label={`ES-${props.equivalentSecurityPillData.listIndex}`}
              className={styles.eqivSecsChip}
            />
          </div>
        </Tooltip>
      )}
    </div>
  );
};

export default SecurityCell;
